import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check, ChevronLeft, ChevronRight, Images, Link2, X } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Loading, PageHero, SectionHead } from "@/components/public/Ui";

function Lightbox({ items, index, onClose, onMove }) {
  const item = items[index];
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onMove(1);
      if (e.key === "ArrowLeft") onMove(-1);
    };
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [index, onClose, onMove]);

  const share = async () => {
    const url = `${window.location.origin}${window.location.pathname}?photo=${item.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt("Copy this photo link", url);
    }
    setCopied(true);
  };

  if (!item) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex flex-col bg-[rgba(7,33,26,0.96)] backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={item.title || item.caption || "Gallery photograph"}
      data-testid="gallery-lightbox"
    >
      <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-8">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-white/60">
          {index + 1} / {items.length}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={share}
            data-testid="gallery-lightbox-share"
            className="inline-flex min-h-[40px] items-center gap-2 rounded-full border border-white/25 px-4 text-xs font-extrabold uppercase tracking-[0.12em] text-white transition-colors hover:border-white/70"
          >
            {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {copied ? "Link copied" : "Share photo"}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close photo"
            data-testid="gallery-lightbox-close"
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-white transition-colors hover:border-white/70"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden px-3 pb-4 sm:px-16"
        onTouchStart={(e) => { Lightbox.touchX = e.changedTouches[0].clientX; }}
        onTouchEnd={(e) => {
          const dx = e.changedTouches[0].clientX - (Lightbox.touchX || 0);
          if (Math.abs(dx) > 50) onMove(dx < 0 ? 1 : -1);
        }}
      >
        <button
          type="button"
          onClick={() => onMove(-1)}
          aria-label="Previous photo"
          data-testid="gallery-lightbox-prev"
          className="absolute left-2 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-[rgba(7,33,26,0.6)] text-white transition-colors hover:border-white/70 sm:left-5"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <img
          src={mediaUrl(item.image_url)}
          alt={item.alt_text || item.caption || item.title || "BKA event photograph"}
          className="max-h-full max-w-full select-none rounded-xl object-contain"
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
        />
        <button
          type="button"
          onClick={() => onMove(1)}
          aria-label="Next photo"
          data-testid="gallery-lightbox-next"
          className="absolute right-2 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-[rgba(7,33,26,0.6)] text-white transition-colors hover:border-white/70 sm:right-5"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {(item.title || item.caption) && (
        <div className="px-6 pb-7 text-center sm:px-16" data-testid="gallery-lightbox-caption">
          {item.title && <p className="font-display text-lg font-bold text-white">{item.title}</p>}
          {item.caption && <p className="mt-1 text-sm leading-relaxed text-white/70">{item.caption}</p>}
        </div>
      )}
    </div>
  );
}

export function GalleryIndex() {
  const [categories, setCategories] = useState(null);

  useEffect(() => {
    api.get("/public/galleries").then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
  }, []);

  if (categories === null) return <Loading />;

  return (
    <>
      <Seo title="Photo Galleries | Bahamas Kidney Association" noindex />
      <PageHero
        overline="BKA Photo Galleries"
        line1="Moments From Our"
        line2="Community"
        subheading="Photographs from Bahamas Kidney Association events, fundraisers and awareness activities across The Bahamas."
      />

      <div className="bka-container py-14 lg:py-20" data-testid="gallery-index">
        {categories.length === 0 ? (
          <p className="text-sm text-bka-ink/70" data-testid="gallery-index-empty">
            Photo galleries are being prepared and will appear here shortly.
          </p>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/gallery/${cat.slug}`}
                data-testid={`gallery-card-${cat.slug}`}
                className="group relative block overflow-hidden rounded-3xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-[0_18px_40px_rgba(11,53,84,0.16)]"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-bka-navy/5">
                  {cat.cover_image ? (
                    <img
                      src={mediaUrl(cat.cover_image)}
                      alt={cat.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-bka-navy/30">
                      <Images className="h-12 w-12" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-bka-navyDeep/85 via-bka-navyDeep/10 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] bka-text-orange">
                      {cat.item_count} photo{cat.item_count === 1 ? "" : "s"}
                    </p>
                    <p className="mt-2 font-display text-2xl font-black leading-tight text-white">{cat.title}</p>
                  </div>
                </div>
                <div className="p-6">
                  {cat.description && (
                    <p className="text-sm leading-relaxed text-bka-ink/75">{cat.description}</p>
                  )}
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-extrabold uppercase tracking-[0.12em] text-bka-emerald">
                    View Gallery
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                  {cat.external_url && (
                    <span className="mt-2 block text-xs text-bka-ink/55">
                      Full album also available on the photographer&apos;s site
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export function GalleryCategory() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(-1);

  useEffect(() => {
    setData(null);
    api.get(`/public/galleries/${slug}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [slug]);

  const photoParam = params.get("photo");
  useEffect(() => {
    if (!data || !photoParam) return;
    const i = (data.items || []).findIndex((it) => it.id === photoParam);
    if (i >= 0) setOpen(i);
  }, [data, photoParam]);

  const close = useCallback(() => {
    setOpen(-1);
    if (params.get("photo")) {
      params.delete("photo");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const move = useCallback(
    (dir) => {
      const total = data?.items?.length || 0;
      if (!total) return;
      setOpen((i) => (i + dir + total) % total);
    },
    [data]
  );

  if (data === null) return <Loading />;
  if (data === false) {
    return (
      <div className="bka-container py-20 text-center" data-testid="gallery-not-found">
        <h1 className="font-display text-3xl font-black text-bka-navy">Gallery not found</h1>
        <Link to="/gallery" className="mt-5 inline-block text-sm font-bold text-bka-emerald">
          Back to all galleries
        </Link>
      </div>
    );
  }

  const { category, items } = data;

  return (
    <>
      <Seo title={`${category.title} | Bahamas Kidney Association`} noindex />
      <PageHero
        overline="BKA Photo Gallery"
        line1={category.title}
        subheading={category.description}
        image={mediaUrl(category.cover_image)}
      />

      <div className="bka-container py-12 lg:py-16" data-testid="gallery-category">
        <Link
          to="/gallery"
          data-testid="gallery-back"
          className="inline-flex items-center gap-2 text-sm font-extrabold uppercase tracking-[0.12em] text-bka-emerald"
        >
          <ArrowLeft className="h-4 w-4" /> All Galleries
        </Link>

        <div className="mt-8">
          <SectionHead
            overline={`${items.length} photograph${items.length === 1 ? "" : "s"}`}
            title={category.title}
          />
        </div>

        {items.length === 0 ? (
          <p className="text-sm text-bka-ink/70" data-testid="gallery-category-empty">
            Photographs from this event are being added and will appear here shortly.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-grid">
            {items.map((item, i) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setOpen(i)}
                data-testid={`gallery-photo-${item.id}`}
                className="group relative aspect-square overflow-hidden rounded-2xl bg-bka-navy/5"
                aria-label={item.title || item.caption || `Open photograph ${i + 1}`}
              >
                <img
                  src={mediaUrl(item.image_url)}
                  alt={item.alt_text || item.caption || item.title || "BKA event photograph"}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                  onContextMenu={(e) => e.preventDefault()}
                />
                {(item.title || item.caption) && (
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-bka-navyDeep/90 to-transparent p-3 text-left text-xs font-semibold leading-snug text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    {item.title || item.caption}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {category.external_url && (
        <div className="bka-container pb-14 lg:pb-20">
          <div className="flex flex-col items-start gap-4 rounded-3xl border border-slate-200 bg-bka-tintSage p-7 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-display text-lg font-black text-bka-navy">Looking for more photographs?</p>
              <p className="mt-1 text-sm leading-relaxed text-bka-ink/75">
                The photographer&apos;s complete album for this event opens in a new tab.
              </p>
            </div>
            <a
              href={category.external_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-external-link"
              className="group inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 rounded-full bg-bka-emerald px-7 text-sm font-extrabold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-bka-emeraldDark"
            >
              {category.external_label || "View the Full Album"}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      )}

      {open >= 0 && items.length > 0 && (
        <Lightbox items={items} index={open} onClose={close} onMove={move} />
      )}
    </>
  );
}

export default GalleryIndex;
